import { ShiftInviteStatus } from "@clipboard-health/contract-backend-main";
import { formatShortDateTime, formatTime, isSameDay } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Button, Chip, type SxProps, type Theme } from "@mui/material";
import { formatDurationBetweenDates } from "@src/appV2/lib/Time/formatDurationBetweenDates";
import { useDeleteShiftInvite } from "@src/appV2/ShiftInvites/api/useDeleteShiftInvite";
import { useGetShiftInvite } from "@src/appV2/ShiftInvites/api/useGetShiftInvite";
import { PACIFIC_TIME_ZONE } from "@src/appV2/Timezone/constants";
import { isPast } from "date-fns";

import { ChatMessageCard } from "./ChatMessageCard";

interface ShiftInviteRequestMessageProps {
  shiftInviteId: string;
  sx?: SxProps<Theme>;
}

interface ShiftInviteChipProps {
  color: "success" | "error" | "default" | "warning";
  label: string;
  customStyles?: Record<string, string>;
}

const GRAY_CHIP_BACKGROUND_COLOR = "rgba(0, 0, 0, 0.08)";

const SHIFT_INVITE_STATUS_CHIP_MAP: Record<ShiftInviteStatus, ShiftInviteChipProps> = {
  [ShiftInviteStatus.PENDING]: {
    color: "warning",
    label: "Pending",
  },
  [ShiftInviteStatus.ACCEPTED]: {
    color: "success",
    label: "Accepted",
  },
  [ShiftInviteStatus.DECLINED]: {
    color: "default",
    label: "Declined",
    customStyles: {
      backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
    },
  },
  [ShiftInviteStatus.EXPIRED]: {
    color: "default",
    label: "Expired",
    customStyles: {
      backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
    },
  },
  [ShiftInviteStatus.DELETED]: {
    color: "default",
    label: "Removed",
    customStyles: {
      backgroundColor: GRAY_CHIP_BACKGROUND_COLOR,
    },
  },
};

/**
 * Format shift start and end time for display.
 */
function formatShiftStartEndDateTime(
  start: Date | string,
  end: Date | string,
  timeZone: string
): string {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const formattedStart = formatShortDateTime(startDate, { timeZone });
  const formattedEnd = formatTime(endDate, { timeZone });

  const doesShiftEndOnSameDay = isSameDay(startDate, endDate, { timeZone });

  return `${formattedStart} - ${formattedEnd}${doesShiftEndOnSameDay ? "" : " (next day)"}`;
}

/**
 * Renders a shift invite request message with rich card UI.
 * Shows shift details, status chip, expiration countdown, and remove action.
 */
export function ShiftInviteRequestMessage(props: ShiftInviteRequestMessageProps) {
  const { shiftInviteId, sx } = props;

  const { data: shiftInviteResponse } = useGetShiftInvite(
    { shiftInviteId },
    { enabled: isDefined(shiftInviteId) }
  );

  const { mutateAsync: deleteShiftInviteMutate, isLoading: isDeletingShiftInvite } =
    useDeleteShiftInvite();

  const shiftInvite = shiftInviteResponse?.data;

  if (!isDefined(shiftInvite)) {
    return null;
  }

  const { status, expiresAt, shiftDetails, facility } = shiftInvite.attributes;

  const isExpired =
    status === ShiftInviteStatus.EXPIRED ||
    (status === ShiftInviteStatus.PENDING && isPast(new Date(expiresAt)));

  const displayDuration = formatDurationBetweenDates({
    fromDate: new Date(),
    toDate: new Date(expiresAt),
    padMinutes: false,
  });

  const timeZone = facility.tmz ?? PACIFIC_TIME_ZONE;
  const formattedShiftTime = formatShiftStartEndDateTime(
    shiftDetails.start,
    shiftDetails.end,
    timeZone
  );

  const isActionable = status === ShiftInviteStatus.PENDING && !isExpired;

  const chipProps = isExpired
    ? SHIFT_INVITE_STATUS_CHIP_MAP[ShiftInviteStatus.EXPIRED]
    : SHIFT_INVITE_STATUS_CHIP_MAP[status];

  const handleRemove = async () => {
    await deleteShiftInviteMutate(shiftInviteId);
  };

  const statusChip = (
    <Chip
      aria-label={chipProps.label}
      color={chipProps.color}
      label={chipProps.label}
      size="small"
      sx={{
        fontWeight: "bold",
        ...chipProps.customStyles,
      }}
      variant="filled"
    />
  );

  const removeButton = isActionable ? (
    <Button
      color="error"
      disabled={isDeletingShiftInvite}
      size="small"
      variant="outlined"
      onClick={handleRemove}
    >
      Remove
    </Button>
  ) : undefined;

  return (
    <ChatMessageCard
      actions={removeButton}
      caption={isActionable ? `Expiring in ${displayDuration}` : undefined}
      statusChip={statusChip}
      subtitle={formattedShiftTime}
      sx={sx}
      title="You sent a shift invite"
    />
  );
}
